
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/toast';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';

// Define schema for form validation
const subscribeSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

type SubscribeFormValues = z.infer<typeof subscribeSchema>;

const BlogSubscribe: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);

  // Initialize form
  const form = useForm<SubscribeFormValues>({
    resolver: zodResolver(subscribeSchema),
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (values: SubscribeFormValues) => {
    setIsLoading(true);
    try {
      // Call Supabase Edge Function
      const { data, error } = await supabase.functions.invoke('subscribe-newsletter', {
        body: JSON.stringify({
          email: values.email,
        }),
      });

      if (error) {
        throw error;
      }

      // Show success message
      toast({
        title: 'Success',
        description: data.message || 'Thank you for subscribing to our newsletter!',
        variant: 'default',
      });

      // Reset form
      form.reset();
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      
      // Show error message
      toast({
        title: 'Subscription Failed',
        description: 'There was a problem subscribing you to our newsletter. Please try again later.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="py-16 bg-white">
      <div className="container-wide">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h2 className="heading-lg mb-4">Subscribe to Our IT Insights Newsletter</h2>
          <p className="text-itmgmt-dark-gray mb-6">
            Get the latest IT management tips, cybersecurity alerts, and technology trends delivered to your inbox monthly.
          </p>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col sm:flex-row gap-4 justify-center">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="flex-grow max-w-md">
                    <FormControl>
                      <Input
                        placeholder="Your email address"
                        type="email"
                        className="px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-itmgmt-medium-blue w-full"
                        aria-label="Email for newsletter subscription"
                        disabled={isLoading}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage className="text-red-500 text-sm text-left" />
                  </FormItem>
                )}
              />
              <Button 
                type="submit" 
                className="button-primary"
                disabled={isLoading}
              >
                {isLoading ? 'Subscribing...' : 'Subscribe'}
              </Button>
            </form>
          </Form>
        </div>
      </div>
    </section>
  );
};

export default BlogSubscribe;
